import { bookCombination, fetchPreviewDataBySlotIds } from "@/api/planner";
import DressTourTimeLine from "@/components/combinations/DressTourTimeLine";
import ReservationDateViewer from "@/components/common/reservation/ReservationDateViewer";
import { GA_EVENT, trackEvent } from "@/utils/gaUtil";
import { getQueryParams } from "@/utils/query";
import { formatDateWithDay } from "@/utils/schedule";
import { formatPhone } from "@/utils/string";
import dayjs from "dayjs";
import { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import ReservationLoadingOverlay from "./ReservationLoadingOverlay";

interface CalendarDay {
  date: Date | null;
  isCurrentMonth: boolean;
}

interface PreviewSlot {
  id: number;
  shopId: number;
  shopName: string;
  startMinuteOfDay: number;
  endMinuteOfDay: number;
  time: string;
  endTime: string;
  roomId: number;
}

interface PreviewCombination {
  slotIds: number[];
  date: string;
  slots: PreviewSlot[];
}

interface PreviewResponse {
  selectedCombinationValid: boolean;
  sameTimelineFallbackAvailable: boolean;
  invalidReason: string | null;
  selectedCombination: PreviewCombination | null;
  alternativeCombinations: PreviewCombination[];
}

function WeddingDatePicker({
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  showUnPlanned = false,
  unPlanned = false,
  onChangeUnPlanned,
  undecidedChecked = false,
  onChangeUndecided,
  typeText = '예식'
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  required?: boolean;
  showUnPlanned?: boolean;
  unPlanned?: boolean;
  onChangeUnPlanned?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  undecidedChecked: boolean;
  onChangeUndecided?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  typeText: string;
}) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(() => {
    if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return new Date(value);
    }
    return new Date();
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) {
      setShowCalendar(false);
    }
  }, [disabled]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const buildCalendarDays = (): CalendarDay[] => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = getDaysInMonth(year, month);
    const daysInPrevMonth = getDaysInMonth(year, month - 1);

    const days: CalendarDay[] = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
      });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ date: new Date(year, month, d), isCurrentMonth: true });
    }
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      days.push({ date: new Date(year, month + 1, d), isCurrentMonth: false });
    }
    return days;
  };

  const handleDayClick = (day: CalendarDay) => {
    if (disabled) return;
    if (!day.date) return;
    const y = day.date.getFullYear();
    const m = String(day.date.getMonth() + 1).padStart(2, "0");
    const d = String(day.date.getDate()).padStart(2, "0");
    onChange(`${y}-${m}-${d}`);
    setShowCalendar(false);
  };

  const changeMonth = (dir: number) => {
    if (disabled) return;
    const d = new Date(calendarDate);
    d.setMonth(d.getMonth() + dir);
    setCalendarDate(d);
  };

  const monthNames = ["1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"];
  const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
  const calDays = buildCalendarDays();

  const selectedDateObj = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(value) : null;

  return (
    <>
      <div className="flex items-center justify-between mb-1.5">
        <label className="block text-xs font-semibold text-gray-700">
          {label} {required && <span className="text-red-400">*</span>}
        </label>
        <div className='flex'>
          {
            showUnPlanned && (
              <label className="flex items-center gap-2 cursor-pointer select-none mr-4">
                <input
                  type="checkbox"
                  checked={unPlanned}
                  onChange={onChangeUnPlanned}
                  className="sr-only peer"
                />
                <span
                  className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                    unPlanned ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"
                  } peer-focus:ring-2 peer-focus:ring-blue-500 peer-focus:ring-offset-1`}
                >
                  {unPlanned && (
                    <i className="ri-check-line text-[10px] text-white"></i>
                  )}
                </span>
                <span className="text-xs font-medium text-gray-600">미진행</span>
              </label>
            )
          }
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={undecidedChecked}
              onChange={onChangeUndecided}
              className="sr-only peer"
            />
            <span
              className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                undecidedChecked ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"
              } peer-focus:ring-2 peer-focus:ring-blue-500 peer-focus:ring-offset-1`}
            >
              {undecidedChecked && (
                <i className="ri-check-line text-[10px] text-white"></i>
              )}
            </span>
            <span className="text-xs font-medium text-gray-600">미정</span>
          </label>
        </div>
      </div>
      <div ref={containerRef} className="relative">
        <div className="flex gap-2">
          <input
            type="text"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            placeholder={disabled ? `${typeText} 예정일 미정` : "2025-03-04"}
            className={`flex-1 px-4 py-3 border rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              disabled
                ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed"
                : "border-gray-200 text-gray-900"
            }`}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => !disabled && setShowCalendar((v) => !v)}
            className={`w-12 h-12 flex items-center justify-center border rounded-xl transition-colors ${
              disabled
                ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                : "border-gray-200 hover:bg-blue-50 hover:border-blue-300 cursor-pointer"
            }`}
          >
            <i className={`ri-calendar-line text-lg ${disabled ? "text-gray-300" : "text-blue-600"}`}></i>
          </button>
        </div>

        {showCalendar && !disabled && (
          <div className="absolute right-0 z-50 bg-white border border-gray-200 rounded-2xl shadow-lg p-4 w-72" style={{ bottom: 50 }}>
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={() => changeMonth(-1)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-gray-600"></i>
              </button>
              <span className="text-sm font-bold text-gray-900">
                {calendarDate.getFullYear()}년 {monthNames[calendarDate.getMonth()]}
              </span>
              <button
                type="button"
                onClick={() => changeMonth(1)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-gray-600"></i>
              </button>
            </div>

            <div className="grid grid-cols-7 mb-1">
              {dayNames.map((d, i) => (
                <div
                  key={d}
                  className={`text-center text-xs font-medium py-1 ${i === 0 ? "text-red-400" : i === 6 ? "text-blue-400" : "text-gray-400"}`}
                >
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {calDays.map((day, idx) => {
                const isSelected =
                  selectedDateObj &&
                  day.date &&
                  day.date.toDateString() === selectedDateObj.toDateString();
                const isToday =
                  day.date && day.date.toDateString() === new Date().toDateString();

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => day.isCurrentMonth && handleDayClick(day)}
                    className={`aspect-square flex items-center justify-center text-xs rounded-lg transition-colors cursor-pointer
                      ${!day.isCurrentMonth ? "text-gray-300 cursor-default" : ""}
                      ${day.isCurrentMonth && !isSelected ? "hover:bg-blue-50 text-gray-700" : ""}
                      ${isSelected ? "bg-blue-600 text-white font-bold" : ""}
                      ${isToday && !isSelected ? "border border-blue-300 font-semibold text-blue-600" : ""}
                    `}
                  >
                    {day.date?.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function parseCsvNumberArray(value: any): number[] {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((v) => Number(v.trim()))
    .filter((v) => Number.isInteger(v) && v > 0);
}

export default function Confirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = getQueryParams(location.search);
  const {
    slotIds,
    selectedShopIds,
    bookingPath
  } = query
  const [reservationCode, setReservationCode] = useState<string | null>(null);
  // const slotIds = useMemo(() => parseCsvNumberArray(query.slotIds || query.slots), [query.slotIds, query.slots]);
  // const selectedShopIds = useMemo(() => parseCsvNumberArray(query.selectedShopIds), [query.selectedShopIds]);
  
  const fixedShopIds = useMemo(() => parseCsvNumberArray(query.fixedShopIds), [query.fixedShopIds]);
  const selectedDate = String(query.date || query.selectedDate || "");
  const tourSize = Number(query.tourSize || 0);

  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const [selectedCombinationIndex, setSelectedCombinationIndex] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [customerName, setCustomerName] = useState("");
  // const [customerPhone, setCustomerPhone] = useState("");
  const [errors, setErrors] = useState<{ name?: string; phone?: string; weddingDate?: string, shootingDate?: string }>({});
  const [bookingLoading, setBookingLoading] = useState(false);

  const [weddingDate, setWeddingDate] = useState("");
  const [weddingDateUndecided, setWeddingDateUndecided] = useState(false);

  const [noShooting, setNoShooting] = useState(false)
  const [shootingDate, setShootingDate] = useState("");
  const [shootingDateUndecided, setShootingDateUndecided] = useState(false);

  const [notice, setNotice] = useState("");
  const [confirmOverlayData, setConfirmOverlayData] = useState(null)
  const authUser = useSelector((state: any) => state.auth.user);

  const plannerDisplayName = authUser?.userName

  
  useEffect(() => {
    let mounted = true;

    async function loadPreview() {
      try {
        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewData(null);
        setSelectedCombinationIndex(0);

        if (!selectedDate || slotIds.length === 0 || selectedShopIds.length === 0 || !tourSize) {
          throw new Error("일정 정보가 올바르지 않습니다.");
        }

        const result = await fetchPreviewDataBySlotIds({
          slotIds,
          date: selectedDate,
          selectedShopIds,
          tourSize,
          fixedShopIds,
        });
        console.log(result)
        if (!mounted) return;

        const data = result?.data?.data || result?.data || result;
        
        setPreviewData(data || null);
      } catch (error: any) {
        console.log(error)
        if (!mounted) return;
        setPreviewError(error?.message || "예약 가능 여부를 확인하지 못했습니다.");
      } finally {
        if (mounted) {
          setPreviewLoading(false);
        }
      }
    }

    loadPreview();

    return () => {
      mounted = false;
    };
  }, [selectedDate, tourSize, slotIds, selectedShopIds, fixedShopIds]);
  const sentConfirmationViewRef = useRef(false);

  useEffect(() => {
    if (sentConfirmationViewRef.current) return;
    const slots = previewData?.selectedCombination?.slots
    if (!slots || slots.length === 0) return;
    
    sentConfirmationViewRef.current = true;
    try {
      trackEvent(GA_EVENT.RESV_CONFIRMATION_VIEW, {
        booking_path: bookingPath,
        slot_count: slots.length,
        shop_count: new Set(slots.map((slot) => slot.shopId)).size,
        selected_date: selectedDate,
      });
    } catch (e) {
      
    }
  }, [bookingPath, previewData]);
  const alternativeCombinations = previewData?.alternativeCombinations || [];

  const combination: PreviewCombination | null = useMemo(() => {
    if (!previewData) return null;

    if (previewData.selectedCombinationValid && previewData.selectedCombination) {
      return previewData.selectedCombination;
    }

    if (
      !previewData.selectedCombinationValid &&
      previewData.sameTimelineFallbackAvailable &&
      alternativeCombinations.length > 0
    ) {
      return alternativeCombinations[selectedCombinationIndex] || alternativeCombinations[0];
    }

    return null;
  }, [previewData, alternativeCombinations, selectedCombinationIndex]);

  


  // const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   setCustomerPhone(formatPhone(e.target.value));
  // };

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!customerName.trim()) newErrors.name = "고객명을 입력해주세요";
    // const digits = customerPhone.replace(/\D/g, "");

    // 010: 반드시 11자리
    // 011/016/017/018/019: 10자리 또는 11자리 허용
    // const phoneRegex = /^(010\d{8}|01[16789]\d{7,8})$/;

    // if (customerPhone.trim() == '') {
    //   newErrors.phone = "고객님 전화번호를 입력해주세요";
    // } else if (!phoneRegex.test(digits)) {
    //   newErrors.phone = "올바른 전화번호를 입력해주세요";
    // }
    

    if (weddingDateUndecided) {}
    else if (dayjs(weddingDate).isBefore(dayjs())) newErrors.weddingDate = "예식 예정일은 오늘 이후로 선택해주세요";
    else if (!weddingDate.trim()) newErrors.weddingDate = "예식 예정일을 입력해주세요";
    else if (!/^\d{4}-\d{2}-\d{2}$/.test(weddingDate)) newErrors.weddingDate = "YYYY-MM-DD 형식으로 입력해주세요";

    if (shootingDateUndecided || noShooting) {}
    else if (dayjs(shootingDate).isBefore(dayjs())) newErrors.shootingDate = "촬영 예정일은 오늘 이후로 선택해주세요";
    else if (!shootingDate.trim()) newErrors.shootingDate = "촬영 예정일을 입력해주세요";
    else if (!/^\d{4}-\d{2}-\d{2}$/.test(shootingDate)) newErrors.shootingDate = "YYYY-MM-DD 형식으로 입력해주세요";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!combination) return;
    if (!validate()) return;
    const slots = previewData?.selectedCombination?.slots
    try {
      setBookingLoading(true);
      
      try {
        trackEvent(GA_EVENT.RESV_SUBMIT, {
          booking_path: bookingPath,
          slot_count: slots.length,
          shop_count: new Set(slots.map((slot) => slot.shopId)).size,
          selected_date: selectedDate,
        });
      } catch (e) {

      }
      const shops = combination.slots.map(slot => ({ id: slot.shopId, shopId: slot.shopId, shopName: slot.shopName, name: slot.shopName }));
      setConfirmOverlayData({  shops });
      const result = await bookCombination({
        slots: combination.slotIds,
        customerName,
        // customerPhone,
        weddingDate,
        shootingDate,
        weddingDateUndecided,
        shootingDateUndecided,
        noShooting,
        notice,
      })
        .catch(e => {
          
          console.log(e)
          return {
            status: e.status,
            message: e?.response?.data?.message
          }
        });
      if (result.status !== 200) {
        setConfirmOverlayData(null)
        alert(result.message)
        return false
      }

      const reservationCode = result?.data?.reservationCode || result?.reservationCode;
      
      if (!reservationCode) {
        throw new Error("예약 코드가 없습니다.");
      }
      try {
        trackEvent(GA_EVENT.RESV_SUCCESS, {
          booking_path: bookingPath,
          slot_count: slots.length,
          reservation_code: result.reservationCode,
          shop_count: new Set(slots.map((slot) => slot.shopId)).size,
          selected_date: selectedDate,
        });
      } catch (e) {

      }
      console.log('sksksksksksk: ', reservationCode)
      setReservationCode(reservationCode);
      return
      
    } catch (error) {
      console.error("bookCombination error", error);
      try {
        trackEvent(GA_EVENT.RESV_FAIL, {
          booking_path: bookingPath,
          slot_count: slots.length,
          shop_count: new Set(slots.map((slot) => slot.shopId)).size,
          selected_date: selectedDate,
          fail_reason: error?.response?.data?.code || "UNKNOWN_ERROR",
        });
      } catch (e) {

      }
    } finally {
      setBookingLoading(false);
    }
  };

  if (previewLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-5">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#2563EB transparent #2563EB #2563EB" }} />
          <p className="text-sm text-gray-500">예약 가능 여부를 확인하고 있습니다</p>
        </div>
      </div>
    );
  }

  if (previewError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-5">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-red-400"></i>
          </div>
          <p className="text-sm text-gray-700 mb-2">{previewError}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            이전으로
          </button>
        </div>
      </div>
    );
  }

  if (!combination) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-5">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-calendar-close-line text-2xl text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-700 mb-1">선택한 일정은 더 이상 예약할 수 없습니다</p>
          <p className="text-xs text-gray-500 mb-4">
            {previewData?.invalidReason ? `사유: ${previewData.invalidReason}` : "동일한 시간대의 대체 일정도 찾지 못했습니다"}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            다시 선택하기
          </button>
        </div>
      </div>
    );
  }

  const showFallbackWarning =
    previewData &&
    !previewData.selectedCombinationValid &&
    previewData.sameTimelineFallbackAvailable &&
    alternativeCombinations.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 py-4 border-b border-gray-100 sticky top-0 z-10">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl text-gray-700"></i>
          </button>
          <h2 className="text-base font-bold text-gray-900">일정 확인 및 예약</h2>
          <div className="w-9"></div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-100 px-5 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
              <i className="ri-check-line text-white text-xs"></i>
            </div>
            <span className="text-xs text-blue-600 font-medium">업체 선택</span>
          </div>
          <div className="flex-1 h-px bg-blue-200"></div>
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
              <i className="ri-check-line text-white text-xs"></i>
            </div>
            <span className="text-xs text-blue-600 font-medium">일정 선택</span>
          </div>
          <div className="flex-1 h-px bg-blue-200"></div>
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
              <span className="text-white text-xs font-bold">3</span>
            </div>
            <span className="text-xs text-blue-600 font-bold">최종 확인</span>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-6 space-y-5 pb-36">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900">예약 전 최종 확인</h1>
            <p className="text-sm text-gray-500 mt-0.5">아래 일정을 확인하고 고객 정보를 입력하면 예약이 진행됩니다</p>
          </div>
          <div className="flex-shrink-0 ml-3 mt-0.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-lg">
            <span className="text-xs font-semibold text-amber-700">미완료</span>
          </div>
        </div>

        {showFallbackWarning && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
            <p className="text-sm font-semibold text-amber-800">
              선택한 일정 중 일부가 먼저 예약되었습니다.
            </p>
            <p className="text-xs text-amber-700 mt-1">
              동일한 시간대의 다른 예약 가능 슬롯으로 변경할 수 있습니다.
            </p>

            {alternativeCombinations.length > 1 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {alternativeCombinations.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSelectedCombinationIndex(index)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer"
                    style={{
                      backgroundColor: selectedCombinationIndex === index ? "#2563EB" : "#fff",
                      color: selectedCombinationIndex === index ? "#fff" : "#92400E",
                      border: "1px solid #F59E0B",
                    }}
                  >
                    대체안 {index + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2" style={{display: 'none'}}>
            <i className="ri-calendar-check-line text-blue-600 text-sm"></i>
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">방문 날짜</span>
          </div>
          <div className="px-5 py-4">
            <ReservationDateViewer targetDate={combination.date} />
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap" style={{display: 'none'}}>
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 rounded-full">
                <i className="ri-store-2-line text-blue-500 text-xs"></i>
                <span className="text-xs font-medium text-blue-700">총 {combination.slots.length}개 업체 방문</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-full">
                <i className="ri-time-line text-gray-500 text-xs"></i>
                <span className="text-xs font-medium text-gray-600">
                  {combination.slots[0]?.time} ~ 종일 일정
                </span>
              </div>
            </div>
          </div>
        </div>

        <DressTourTimeLine slots={combination.slots} />
        {/* <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-route-line text-blue-600 text-sm"></i>
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">방문 일정 타임라인</span>
            </div>
            <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-0.5 rounded-full">
              {combination.slots.length}개 업체
            </span>
          </div>
          <div className="px-5 py-5">
            <div className="space-y-0">
              {combination.slots.map((slot, index) => (
                <div key={`${slot.id}_${index}`} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 pt-1">
                    <span className="text-sm font-bold text-gray-900">{slot.time}</span>
                  </div>

                  <div className="flex flex-col items-center flex-shrink-0" style={{paddingTop: 9}}>
                    <div className="w-3 h-3 rounded-full bg-blue-600 border-2 border-white ring-2 ring-blue-200 mt-0.5"></div>
                    {index < combination.slots.length - 1 && (
                      <div className="w-0.5 h-20 bg-gradient-to-b from-blue-300 to-blue-100 my-0.5"></div>
                    )}
                  </div>

                  <div className={`flex-1 ${index < combination.slots.length - 1 ? "pb-4" : ""}`}>
                    <div className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-gray-900">{slot.shopName}</p>
                            <span className="flex-shrink-0 text-xs text-gray-400 font-normal">방문 1시간</span>
                          </div>
                          <div className="mt-1.5 flex items-center gap-1">
                            <a
                              href="#"
                              onClick={(e) => e.preventDefault()}
                              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 transition-colors cursor-pointer group"
                            >
                              <i className="ri-file-text-line text-xs group-hover:text-blue-500"></i>
                              <span className="underline underline-offset-2 decoration-dashed">위약금 안내</span>
                            </a>
                          </div>
                        </div>
                        <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-store-2-line text-blue-500 text-sm"></i>
                        </div>
                      </div>
                    </div>
                    {index < combination.slots.length - 1 && (
                      <div className="flex items-center gap-2 mt-2 ml-1">
                        <i className="ri-arrow-down-line text-gray-300 text-xs"></i>
                        <span className="text-xs text-gray-400">이동 1시간</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div> */}

        <div className="bg-white rounded-2xl border border-gray-100">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
            <i className="ri-user-line text-blue-600 text-sm"></i>
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">고객 정보 입력</span>
            <span className="ml-auto text-xs text-red-400 font-medium">* 필수 입력</span>
          </div>
          <div className="px-5 pt-4 pb-1">
            <p className="text-xs text-gray-500">예약 확정을 위해 아래 정보를 정확히 입력해주세요. 입력하신 연락처로 예약 확인 안내가 발송됩니다.</p>
          </div>
          {/* <div className="px-5 py-4 space-y-4" style={{display: 'none'}}>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                고객명 <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => {
                  setCustomerName(e.target.value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                }}
                placeholder="예: 김지수"
                className={`w-full px-4 py-3 border rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  errors.name ? "border-red-300 bg-red-50" : "border-gray-200"
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                연락처 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={handlePhoneChange}
                  placeholder="010-0000-0000"
                  maxLength={13}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.phone ? "border-red-300 bg-red-50" : "border-gray-200"
                  }`}
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.phone}
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                예식 예정일 <span className="text-red-400">*</span>
              </label>
              <WeddingDatePicker
                label='예식 예정일'
                value={weddingDate}
                onChange={(v) => {
                  setWeddingDate(v);
                  if (errors.weddingDate) setErrors((prev) => ({ ...prev, weddingDate: undefined }));
                }}
              />
              {errors.weddingDate && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.weddingDate}
                </p>
              )}
              <p className="mt-1.5 text-xs text-gray-400">직접 입력하거나 캘린더 아이콘을 눌러 선택하세요</p>
            </div>
          </div> */}
          <div className="px-5 py-4 space-y-4">
            <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center flex-shrink-0 border border-blue-100">
                  <i className="ri-user-star-line text-blue-600 text-base"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-500">
                    담당 플래너
                  </p>
                  <p className="text-sm font-bold text-gray-900 mt-0.5">
                    {plannerDisplayName}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    본 일정 예약을 진행하는 담당자입니다
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                고객명 <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => {
                  setCustomerName(e.target.value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                }}
                placeholder="예: 김지수"
                className={`w-full px-4 py-3 border rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  errors.name ? "border-red-300 bg-red-50" : "border-gray-200"
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.name}
                </p>
              )}
            </div>

            {/* <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                고객 연락처 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={handlePhoneChange}
                  placeholder="010-0000-0000"
                  maxLength={13}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.phone ? "border-red-300 bg-red-50" : "border-gray-200"
                  }`}
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.phone}
                </p>
              )}
            </div> */}

            <div>
              <WeddingDatePicker
                label='예식 예정일'
                typeText="예식"
                required
                value={weddingDate}
                disabled={weddingDateUndecided}
                undecidedChecked={weddingDateUndecided}
                onChangeUndecided={(e) => {
                  const checked = e.target.checked;
                  setWeddingDateUndecided(checked);

                  if (checked) {
                    setWeddingDate("");
                    setErrors((prev) => ({ ...prev, weddingDate: undefined }));
                  }
                }}
                onChange={(v) => {
                  setWeddingDate(v);
                  if (errors.weddingDate) setErrors((prev) => ({ ...prev, weddingDate: undefined }));
                }}
              />

              {errors.weddingDate && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.weddingDate}
                </p>
              )}

              <p className="mt-1.5 text-xs text-gray-400">
                {weddingDateUndecided
                  ? "예식 예정일이 아직 정해지지 않은 상태로 예약을 진행합니다"
                  : "직접 입력하거나 캘린더 아이콘을 눌러 선택하세요"}
              </p>
            </div>
            <div>
              <WeddingDatePicker
                label='촬영 예정일'
                typeText="촬영"
                required
                showUnPlanned
                unPlanned={noShooting}
                onChangeUnPlanned={(e) => {
                  const checked = e.target.checked;
                  setNoShooting(checked);
                  setShootingDateUndecided(false);
                  if (checked) {
                    setShootingDate("");
                    setErrors((prev) => ({ ...prev, shootingDate: undefined }));
                  }
                }}
                value={shootingDate}
                disabled={noShooting || shootingDateUndecided}
                undecidedChecked={shootingDateUndecided}
                onChangeUndecided={(e) => {
                  const checked = e.target.checked;
                  setShootingDateUndecided(checked);
                  setNoShooting(false)
                  if (checked) {
                    setShootingDate("");
                    setErrors((prev) => ({ ...prev, shootingDate: undefined }));
                  }
                }}
                onChange={(v) => {
                  setShootingDate(v);
                  if (errors.shootingDate) setErrors((prev) => ({ ...prev, shootingDate: undefined }));
                }}
              />

              {errors.shootingDate && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.shootingDate}
                </p>
              )}

              <p className="mt-1.5 text-xs text-gray-400">
                {shootingDateUndecided
                  ? "촬영 예정일이 아직 정해지지 않은 상태로 예약을 진행합니다"
                  : "직접 입력하거나 캘린더 아이콘을 눌러 선택하세요"}
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                비고(예약 업체들에 전달될 메시지)
              </label>

              <textarea
                value={notice}
                onChange={(e) => setNotice(e.target.value)}
                placeholder="업체에 전달할 요청사항이나 참고 메모를 입력해주세요"
                rows={4}
                maxLength={300}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />

              <div className="mt-0.5 flex items-center justify-end">
                
                <span className="text-xs text-gray-400">
                  {notice.length}/300
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 rounded-2xl p-5 border border-blue-100" style={{display: 'none'}}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-blue-600 text-sm"></i>
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-blue-900 mb-2">예약 전 안내사항</p>
              <ul className="text-xs text-blue-800 space-y-1.5">
                <li className="flex items-start gap-1.5">
                  <span className="text-blue-400 mt-0.5">•</span>
                  <span>각 업체 방문은 약 1시간이 소요됩니다</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-blue-400 mt-0.5">•</span>
                  <span>업체 간 이동 시간은 1시간으로 계산됩니다</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-blue-400 mt-0.5">•</span>
                  <span>예약 확정 후 업체에서 개별 연락드립니다</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-blue-400 mt-0.5">•</span>
                  <span>일정 변경이 필요한 경우 최소 3일 전에 연락해주세요</span>
                </li>
              </ul>
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-xs text-blue-700">
                  <i className="ri-shield-check-line mr-1"></i>
                  각 업체별 위약금 정책은 타임라인의 <strong>위약금 안내</strong> 링크에서 확인하세요
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-5 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex-shrink-0 px-5 py-4 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-arrow-left-line text-sm"></i>
              다시 선택
            </button>
            <button

              onClick={handleSubmit}
              disabled={bookingLoading} 
              className="flex-1 py-4 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <i className="ri-calendar-check-line text-base"></i>
              {bookingLoading ? "예약 진행 중..." : "예약 진행하기"}
            </button>
          </div>
          <p className="text-center text-xs text-gray-400 mt-2">고객 정보 입력 후 예약이 진행됩니다</p>
        </div>
      </div>
      <ReservationLoadingOverlay 
        isOpen={!!confirmOverlayData} 
        reservationCode={reservationCode} 
        shops={confirmOverlayData?.shops || []} 
      />
    </div>
  );
}